import { useEffect, useMemo, useState } from 'react'
import { usePageTitle } from '../AppLayout'
import { useHasPermission } from '../api/session'
import { useEventFeed } from '../hooks/useEventFeed'
import { KANAL_LABEL, fussAus, gruppenAus, uhr } from '../hooks/feedRegeln'
import { Loading } from '../components/common/Feedback'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { FeedTafel } from '../components/feed/FeedTafel'

/** Was der Feed zeigt. Die Rechte werden einzeln abgefragt — siehe unten. */
const KANAELE = [
  { topic: 'news', recht: 'news:read' },
  { topic: 'research', recht: 'research:read' },
  { topic: 'event', recht: 'market_event:read' },
  { topic: 'assessment', recht: 'assessment:read' },
  { topic: 'trade_idea', recht: 'trade_idea:read' },
] as const

/**
 * Wie oft die Tafel ihre Uhr stellt.
 *
 * „Heute" wird um Mitternacht zu „Gestern", und die Kante für Neues wandert
 * um 18:00 — beides ohne dass ein Eintrag käme. Auf einem Bildschirm, der
 * tagelang läuft, muss die Tafel das von selbst merken.
 */
const UHR_TAKT = 60_000

/**
 * Der Feed — der letzte Stand, und was seither dazukommt.
 *
 * Fünf Kanäle: Meldungen und Fälle mit ihren jüngsten Einträgen; Termine,
 * Einschätzungen und Ideen nur von heute. Beim Öffnen einmal geladen, über
 * dieselben Endpunkte wie die Listen; danach hält der Ereignisstrom ihn
 * aktuell. Die Regeln, was aus einem Eintrag eine Zeile macht, stehen in
 * `hooks/feedRegeln.ts`; die Tafel selbst in `components/feed/FeedTafel`.
 *
 * Sortiert wird nach ERSCHEINUNGSDATUM, nicht nach Eintreffzeitpunkt: Eine
 * Meldung, die heute eingetragen wird, aber gestern erschien, steht bei
 * gestern. Damit steht der Feed in derselben Zeitrechnung wie die Listen.
 */
export function FeedPage() {
  // Die Rechte EINZELN abgefragt und nicht in einer Schleife: Ein Hook darf
  // nicht bedingt oder wiederholt aufgerufen werden, sonst gerät Reacts
  // Buchführung durcheinander.
  const rechte: Record<string, boolean> = {
    'news:read': useHasPermission('news:read'),
    'research:read': useHasPermission('research:read'),
    'market_event:read': useHasPermission('market_event:read'),
    'assessment:read': useHasPermission('assessment:read'),
    'trade_idea:read': useHasPermission('trade_idea:read'),
  }

  const erlaubt = KANAELE.filter((k) => rechte[k.recht])
  const topics = erlaubt.map((k) => k.topic)

  const { entries, loading } = useEventFeed(topics, topics.length > 0)

  // Wann die Tafel zuletzt etwas bekam — steht in der Kopfzeile, damit man
  // sieht, dass sie lebt.
  const [stand, setStand] = useState<Date | null>(null)
  useEffect(() => { if (!loading) setStand(new Date()) }, [entries, loading])

  const [takt, setTakt] = useState(0)
  useEffect(() => {
    const timer = window.setInterval(() => setTakt((n) => n + 1), UHR_TAKT)
    return () => window.clearInterval(timer)
  }, [])

  // `takt` steht in den Abhängigkeiten, damit „Heute" und die Kante mit der
  // Uhr gehen — nicht nur, wenn ein Eintrag kommt.
  const gruppen = useMemo(() => gruppenAus(entries), [entries, takt])

  usePageTitle('Feed', erlaubt.map((k) => KANAL_LABEL[k.topic]).join(', ')
    + (stand ? ` · zuletzt ${uhr(stand)}` : ''))

  if (topics.length === 0) {
    return <PermissionNotice permission="news:read" subject="den Feed" />
  }

  if (loading && entries.length === 0) {
    return <Loading>lädt den Stand …</Loading>
  }

  return (
    <FeedTafel gruppen={gruppen} fuss={fussAus(gruppen)}
               leer="Keine Meldungen, Fälle, Termine, Einschätzungen oder Ideen im Bestand. Sobald etwas eintrifft, erscheint es hier." />
  )
}
